import { startOfDay } from 'date-fns/startOfDay';
import isNil from 'lodash/isNil';

import { appendSearchCriteriaParamsToPath } from '@nocowanie/common-ui/helpers';
import { SearchEngineModel } from '@nocowanie/common-ui/models';

import { environment } from '@app/environments/environment';
import { GeoLocationData } from '@app/helpers/autocomplete.helpers';
import { getLegacyListingUrl } from '@app/helpers/legacy-url-mapping.helpers';
import { formatSubmittedDates, ResolvedLocationData } from '@app/helpers/search.helpers';
import { SearchCriteria, SearchFilters } from '@app/models';
import { AutocompleteItemModel } from '@app/models/elastic-search';

import { ResolveDatesParams, ResolveFlexDatesParams } from './listing-search-bar.props';

// ---------------------------------------------------------------------------
// Form initialisation helpers
// ---------------------------------------------------------------------------

export function resolveFormDates({
    isInitialDataLoaded,
    checkInDate,
    checkOutDate,
    cookieCheckInDate,
    cookieCheckOutDate,
    cmsCheckIn,
    cmsCheckOut,
}: ResolveDatesParams): { checkInDate: Date | null; checkOutDate: Date | null } {
    if (!isInitialDataLoaded) {
        return { checkInDate: null, checkOutDate: null };
    }

    const resolvedCheckIn = (() => {
        switch (true) {
            case !!checkInDate:
                return startOfDay(new Date(checkInDate!));
            case !!cookieCheckInDate:
                return cookieCheckInDate!;
            case !!cmsCheckIn:
                return startOfDay(new Date(cmsCheckIn!));
            default:
                return null;
        }
    })();

    const resolvedCheckOut = (() => {
        switch (true) {
            case !!checkOutDate:
                return startOfDay(new Date(checkOutDate!));
            case !!cookieCheckOutDate:
                return cookieCheckOutDate!;
            case !!cmsCheckOut:
                return startOfDay(new Date(cmsCheckOut!));
            default:
                return null;
        }
    })();

    return { checkInDate: resolvedCheckIn, checkOutDate: resolvedCheckOut };
}

export function resolveFormFlexDates({
    urlFlexDates,
    cookieFlexDates,
    cmsFlexDates,
}: ResolveFlexDatesParams): number | null {
    switch (true) {
        case !isNil(urlFlexDates):
            return urlFlexDates!;
        case !isNil(cookieFlexDates):
            return cookieFlexDates!;
        case !isNil(cmsFlexDates):
            return cmsFlexDates!;
        default:
            return environment.DEFAULT_FLEX_DATES;
    }
}

// ---------------------------------------------------------------------------
// Submit strategy helpers
// ---------------------------------------------------------------------------

export function buildFinalSearchData(
    searchEngineData: SearchEngineModel,
    resolvedLocation: ResolvedLocationData,
): SearchEngineModel {
    return {
        ...searchEngineData,
        location: resolvedLocation.updatedLocation,
    };
}

export function handleAccommodationRedirect({
    resolvedItem,
    finalData,
    setCookie,
    handleItemSelection,
    getSearchCriteriaFromCookie,
}: {
    resolvedItem: AutocompleteItemModel;
    finalData: SearchEngineModel;
    setCookie: (data: SearchEngineModel) => void;
    handleItemSelection: (item: AutocompleteItemModel, silent?: boolean) => boolean;
    getSearchCriteriaFromCookie: () => Partial<SearchEngineModel> | null;
}): void {
    setCookie(finalData);
    if (!resolvedItem.details?.ota) {
        handleItemSelection(resolvedItem, true);
    } else {
        const targetPath = appendSearchCriteriaParamsToPath(
            resolvedItem.path,
            getSearchCriteriaFromCookie(),
        );
        window.location.href = targetPath;
    }
}

export function isCriteriaUnchanged({
    finalData,
    currentCriteria,
}: {
    finalData: SearchEngineModel;
    currentCriteria: SearchCriteria;
}): boolean {
    const { adultsCount, childrenCount } = finalData.guests;
    const { checkIn: checkInDate, checkOut: checkOutDate } = formatSubmittedDates(finalData);
    return (
        checkInDate === currentCriteria.checkInDate &&
        checkOutDate === currentCriteria.checkOutDate &&
        adultsCount === currentCriteria.guests.adultsCount &&
        childrenCount === currentCriteria.guests.childrenCount &&
        JSON.stringify(finalData.guests.childrenAges) ===
            JSON.stringify(currentCriteria.guests.childrenAges) &&
        finalData.flexDates === currentCriteria.flexDates
    );
}

export function buildInPageUpdatePayload(finalData: SearchEngineModel) {
    const { adultsCount, childrenCount } = finalData.guests;
    const { checkIn: checkInDate, checkOut: checkOutDate } = formatSubmittedDates(finalData);
    return {
        checkInDate,
        checkOutDate,
        guests: {
            adultsCount,
            childrenCount,
            childrenAges: finalData.guests.childrenAges || [],
            guestCount: adultsCount + childrenCount,
        },
        flexDates: finalData.flexDates,
    };
}

export function buildLegacyRedirectParams({
    finalData,
    locationData,
    resolvedItem,
    searchCriteria,
    filters,
}: {
    finalData: SearchEngineModel;
    locationData: GeoLocationData | null;
    resolvedItem: AutocompleteItemModel | undefined;
    searchCriteria: SearchCriteria;
    filters: Partial<SearchFilters>;
}): string {
    const { checkIn: checkInDate, checkOut: checkOutDate } = formatSubmittedDates(finalData);
    const legacySearchCriteria = {
        location: finalData.location,
        checkInDate,
        checkOutDate,
        guests: {
            adultsCount: finalData.guests.adultsCount,
            childrenCount: finalData.guests.childrenCount,
            childrenAges: finalData.guests.childrenAges || [],
            guestCount: finalData.guests.adultsCount + finalData.guests.childrenCount,
        },
        geoId: locationData?.geoId || searchCriteria.geoId || null,
        locationType: locationData?.locationType || searchCriteria.locationType || null,
        flexDates: finalData.flexDates,
    };

    const locationDetails = resolvedItem
        ? {
              name: resolvedItem.name,
              type: resolvedItem.type,
              path: resolvedItem.path,
              details: {
                  location: resolvedItem.details?.location ?? { lon: 0, lat: 0 },
                  radius: resolvedItem.details?.radius ?? 0,
                  regionId: resolvedItem.details?.regionId,
              },
          }
        : null;

    return getLegacyListingUrl(legacySearchCriteria, filters, locationDetails);
}
